import LangEnum from '../../../../lang/LangEnum';
import snackbarStore from '../../../../stores/Snackbar.store';
import { SnackbarMessageType } from '../../../../stores/snackbar.types';
import { useTranslation } from '../../../translation/TranslationContext';
import { useNetworkContext } from '../../contexts/network/network.context';
import { existsIndexDbToUser } from '../../database/offlineDatabase/offlineDatabase';
import { useOfflineOrdersStore } from '../../stores/offlineOrders/offlineOrders.store';
import { ValidateSyncingOrdersAndReturnResultOutput } from '../../stores/offlineOrders/types/offlineOrders.actions.types';

interface UseOfflineOrdersSyncingHandlerParams {
  onAnyChange?: (result: ValidateSyncingOrdersAndReturnResultOutput) => void;
}

const useOfflineOrdersSyncingHandler = ({ onAnyChange }: UseOfflineOrdersSyncingHandlerParams) => {
  const { handleMessage } = snackbarStore;
  const { translate } = useTranslation();
  const { userHasInternet } = useNetworkContext();

  const offlineOrdersStore = useOfflineOrdersStore();

  const generateFeedbackMessage = (
    codes: string[],
    singleMessage: LangEnum,
    pluralMessage: LangEnum,
  ) =>
    codes.length === 1
      ? translate(singleMessage, { code: `${codes[0]}*` })
      : translate(pluralMessage, { quantity: codes.length });

  const generateFeedbacks = (syncResult: ValidateSyncingOrdersAndReturnResultOutput) => {
    const feedbackConfigs = [
      {
        codes: syncResult.syncedOfflineOrders.map((order) => String(order.code)),
        single: LangEnum.OFFLINE_FEEDBACK_SYNCED,
        plural: LangEnum.OFFLINE_FEEDBACK_SYNCED_PLURAL,
        type: 'success' as SnackbarMessageType,
      },
      {
        codes: syncResult.partiallySyncedOfflineOrders.map((order) => String(order.code)),
        single: LangEnum.OFFLINE_FEEDBACK_PARTIALLY_SYNCED,
        plural: LangEnum.OFFLINE_FEEDBACK_PARTIALLY_SYNCED_PLURAL,
        type: 'warning' as SnackbarMessageType,
      },
      {
        codes: syncResult.unsyncedOfflineOrders.map((order) => String(order.code)),
        single: LangEnum.OFFLINE_FEEDBACK_CANT_SYNC,
        plural: LangEnum.OFFLINE_FEEDBACK_CANT_SYNC_PLURAL,
        type: 'error' as SnackbarMessageType,
      },
    ];

    const feedbackWithCodes = feedbackConfigs.filter((config) => config.codes.length);

    feedbackWithCodes.forEach((config, index) => {
      if (config.codes.length) {
        const message = generateFeedbackMessage(config.codes, config.single, config.plural);
        setTimeout(() => {
          handleMessage({ type: config.type, message });
        }, index * 4000);
      }
    });
  };

  const handleWithOfflineOrdersSyncing = async (offlineOrderIds?: string[]) => {
    const hasIndexDb = await existsIndexDbToUser();
    const canSyncOfflineOrders = hasIndexDb && userHasInternet;

    if (!canSyncOfflineOrders) {
      return;
    }

    try {
      const syncResult =
        await offlineOrdersStore.validateSyncingOrdersAndReturnResult(offlineOrderIds);

      generateFeedbacks(syncResult);

      const hasAnyChange =
        !!syncResult.syncedOfflineOrders.length ||
        !!syncResult.partiallySyncedOfflineOrders.length ||
        !!syncResult.unsyncedOfflineOrders.length;

      if (hasAnyChange && onAnyChange) {
        onAnyChange(syncResult);
      }
    } catch (error) {
      console.error('syncOfflineOrders', { error });
    }
  };

  return { handleWithOfflineOrdersSyncing };
};

export default useOfflineOrdersSyncingHandler;
